// Verbatim port of parseDataverseItem from the legacy index.html.
// Three-tier categorization: absolute title rules -> feed-based -> title keywords.
// The regex and rule ordering encode multiple production bug fixes;
// do not reorder or "simplify" without running the tests.

import { MS_UPDATE_TYPES, SUBCATEGORIES } from './constants';
import { activityDateOf, dateStringOrNull } from './itemDates';
import { reportNonFatal } from './observability/reportNonFatal';
import type {
  Audience,
  Category,
  DataverseRow,
  Impact,
  ParsedItem,
  PostType,
  Topic,
  UpdateType,
} from './types';

const AUDIENCES: readonly Audience[] = ['end_users', 'admins', 'developers'];

// Allowed community topic / post-type values, in display order.
export const TOPICS: readonly Topic[] = [
  'copilot',
  'copilot-studio',
  'purview',
  'security',
  'sharepoint-teams',
  'power-platform',
  'azure-ai',
  'devices',
  'other',
];
export const POST_TYPES: readonly PostType[] = ['how-to', 'deep-dive', 'opinion', 'event', 'news'];

// Community blog-summary `topic`; anything outside the known set is 'other'.
export function topicOrOther(value: unknown): Topic {
  return typeof value === 'string' && (TOPICS as readonly string[]).includes(value)
    ? (value as Topic)
    : 'other';
}

// Community blog-summary `post_type`; anything outside the known set is 'news'.
export function postTypeOrNews(value: unknown): PostType {
  return typeof value === 'string' && (POST_TYPES as readonly string[]).includes(value)
    ? (value as PostType)
    : 'news';
}

// True for the community marker in clarito_sourcetype / the summary `kind`
// (trimmed, any case). Empty, null and anything else mean official.
export function isCommunityMarker(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === 'community';
}

// Blogger name from the summary JSON: trimmed, capped, null when unusable.
const AUTHOR_MAX_CHARS = 128;
function authorOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text ? text.slice(0, AUTHOR_MAX_CHARS) : null;
}

// Defensive cap for analysis_fi. The prompt asks for 2-4 sentences; anything
// far longer is a malformed answer and must not blow up the card.
export const ANALYSIS_MAX_CHARS = 1200;

// analysis_fi from the AI summary JSON: trimmed, capped, null when missing,
// not a string, or empty. Older rows never carry it.
function analysisOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text) return null;
  return text.length > ANALYSIS_MAX_CHARS
    ? `${text.slice(0, ANALYSIS_MAX_CHARS - 1).trimEnd()}…`
    : text;
}

// audience from the AI summary JSON; anything outside the known set is null.
function audienceOrNull(value: unknown): Audience | null {
  return typeof value === 'string' && (AUDIENCES as readonly string[]).includes(value)
    ? (value as Audience)
    : null;
}

// Pull an absolute retirement/deprecation date out of a feed title. Matches
// "September 30, 2026" (with optional leading "on|by|before") or "MM/DD/YYYY".
// Returns null when the title has no parseable date — callers fall back to
// publishedDate where appropriate.
function parseRetireDate(title: string): Date | null {
  const m =
    title.match(
      /(?:on |by |before )?(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}/i,
    ) || title.match(/\d{1,2}\/\d{1,2}\/\d{4}/);
  if (!m) return null;
  const d = new Date(m[0]);
  return isNaN(d.getTime()) ? null : d;
}

const MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Finnish month names with the inflection forms the AI summary actually uses
// ("kesäkuussa 2026" = "in June 2026"). Each entry is the month stem; the
// regex below tolerates the kuu/kuun/kuussa/kuuhun endings.
const MONTHS_FI_STEMS = [
  'tammi',
  'helmi',
  'maalis',
  'huhti',
  'touko',
  'kesä',
  'heinä',
  'elo',
  'syys',
  'loka',
  'marras',
  'joulu',
];

// Microsoft 365 roadmap items prefix the title with the rollout window in
// brackets, e.g. "[Rolling out: May 2026] Outlook: ICS preview". The RSS
// publishedDate is when the item was *added* to the roadmap, not when it
// actually rolls out — so without this, every M365 roadmap item lands in the
// wrong column on the timeline.
//
// Resolution order:
//   1. Title bracket prefix — most reliable, used by Azure Updates / parts of
//      M365 Roadmap.
//   2. Finnish month + year mentioned in the summary, gated to plausible
//      future dates only — covers M365 Roadmap items where the rollout date
//      lives in the description rather than the title and the AI surfaces it
//      in summaryFI ("Käyttöönotto alkaa kesäkuussa 2026").
//
// Returns null when nothing matches; the caller falls back to publishedDate.
function parseRolloutDate(
  title: string,
  summaryFI?: string,
  publishedDate?: string,
): Date | null {
  const fromBracket = parseTitleBracket(title);
  if (fromBracket) return fromBracket;

  if (summaryFI) {
    const fromSummary = parseFutureDateInText(summaryFI, publishedDate);
    if (fromSummary) return fromSummary;
  }

  return null;
}

function parseTitleBracket(title: string): Date | null {
  const bracket = title.match(
    /\[(?:Rolling out|In Development|In Preview|Launched|Generally Available)\s*:\s*([^\]]+)\]/i,
  );
  if (!bracket) return null;
  const inner = bracket[1];
  const months = MONTHS_EN.join('|');

  // Form 1a: month range like "May - June 2026" — trailing year applies to
  // both ends, anchor on the start month.
  const monthRange = inner.match(
    new RegExp(`(${months})\\s*(?:-|–|to)\\s*(?:${months})\\s+(\\d{4})`, 'i'),
  );
  if (monthRange) {
    const d = new Date(`${monthRange[1]} 1, ${monthRange[2]}`);
    if (!isNaN(d.getTime())) return d;
  }

  // Form 1b: explicit month + year, e.g. "May 2026".
  const monthYear = inner.match(new RegExp(`(${months})\\s+(\\d{4})`, 'i'));
  if (monthYear) {
    const d = new Date(`${monthYear[1]} 1, ${monthYear[2]}`);
    if (!isNaN(d.getTime())) return d;
  }

  // Form 2: quarter notation. Microsoft uses both orders:
  //   "CY26 Q3", "CY2026 Q3"  (year-first)
  //   "Q3 CY26", "Q3 2026"    (quarter-first)
  // Map each quarter to its start month: Q1→Jan, Q2→Apr, Q3→Jul, Q4→Oct.
  const yearFirst = inner.match(/(?:CY)?(\d{2,4})\s*Q([1-4])/i);
  const quarterFirst = inner.match(/Q([1-4])\s*(?:CY)?(\d{2,4})/i);
  if (yearFirst || quarterFirst) {
    const yearRaw = yearFirst ? yearFirst[1] : quarterFirst![2];
    const qStr = yearFirst ? yearFirst[2] : quarterFirst![1];
    const year = yearRaw.length === 2 ? 2000 + parseInt(yearRaw) : parseInt(yearRaw);
    const q = parseInt(qStr);
    if (year >= 2020 && year <= 2100 && q >= 1 && q <= 4) {
      return new Date(year, (q - 1) * 3, 1);
    }
  }

  return null;
}

// Try to find a future month + year mentioned anywhere in `text`. Used as a
// secondary fallback for M365 Roadmap items where the title carries no
// bracket but the AI summary mentions e.g. "kesäkuussa 2026". Conservative
// gating: the date must be in the future *and* within ~24 months of the
// item's publishedDate, so a casual mention of a past release ("jatkoa
// kesäkuun 2024 versiolle...") doesn't move the item to the wrong column.
function parseFutureDateInText(text: string, publishedDate?: string): Date | null {
  const now = Date.now();
  const pubMs = publishedDate ? new Date(publishedDate).getTime() : now;
  const maxFutureMs = (isNaN(pubMs) ? now : pubMs) + 24 * 30 * 86_400_000;

  const candidates: Date[] = [];

  // Finnish months: stem + (kuu|kuun|kuussa|kuuhun) + " " + year.
  const fiPattern = new RegExp(
    `\\b(${MONTHS_FI_STEMS.join('|')})kuu(?:n|ssa|hun)?\\s+(\\d{4})`,
    'gi',
  );
  for (const m of text.matchAll(fiPattern)) {
    const stem = m[1].toLowerCase();
    const monthIdx = MONTHS_FI_STEMS.indexOf(stem);
    if (monthIdx === -1) continue;
    const year = parseInt(m[2]);
    if (year < 2020 || year > 2100) continue;
    candidates.push(new Date(year, monthIdx, 1));
  }

  // English months — covers AI summaries that didn't translate the date.
  const enPattern = new RegExp(`\\b(${MONTHS_EN.join('|')})\\s+(\\d{4})`, 'gi');
  for (const m of text.matchAll(enPattern)) {
    const monthIdx = MONTHS_EN.findIndex(
      (name) => name.toLowerCase() === m[1].toLowerCase(),
    );
    if (monthIdx === -1) continue;
    const year = parseInt(m[2]);
    if (year < 2020 || year > 2100) continue;
    candidates.push(new Date(year, monthIdx, 1));
  }

  // Pick the earliest plausible future date — items advertise their next
  // rollout, not the one after.
  const futureCandidates = candidates
    .filter((d) => d.getTime() > now && d.getTime() <= maxFutureMs)
    .sort((a, b) => a.getTime() - b.getTime());
  return futureCandidates[0] ?? null;
}

export function parseDataverseItem(raw: DataverseRow): ParsedItem | null {
  try {
    let summaryFI = (raw.cr436_summaryfi as string) || (raw.SummaryFI as string) || '';
    let impact: Impact = 'medium';
    let updateType: UpdateType = 'update';
    let actionNeeded = false;
    // AI-extracted effective_date wins over title-regex / summary-parsing
    // fallbacks when present. Stays null when the AI didn't find a clear
    // date in the article.
    let aiEffectiveDate: string | null = null;
    // Organisation analysis + audience; only new and changed items have them.
    let analysisFi: string | null = null;
    let audience: Audience | null = null;
    // Community (independent-blogger) post: the row's clarito_sourcetype or
    // the summary's `kind` marks it. Topic and post type use their own field
    // names; `type` is the official update type.
    let community = isCommunityMarker(raw.clarito_sourcetype);
    let rawTopic: unknown;
    let rawPostType: unknown;
    let author: string | null = null;

    // AI Builder returns its payload as a JSON blob inside cr436_summaryfi.
    // Strip code fences, parse, and if it looks like our shape, extract fields.
    try {
      const cleaned = summaryFI.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(cleaned) as {
        summary_fi?: string;
        impact?: Impact;
        type?: UpdateType;
        action_needed?: boolean;
        effective_date?: string | null;
        analysis_fi?: unknown;
        audience?: unknown;
        kind?: unknown;
        topic?: unknown;
        post_type?: unknown;
        author?: unknown;
      };
      if (parsed.summary_fi) summaryFI = parsed.summary_fi;
      if (parsed.impact) impact = parsed.impact;
      if (parsed.type) updateType = parsed.type;
      if (parsed.action_needed !== undefined) actionNeeded = !!parsed.action_needed;
      if (typeof parsed.effective_date === 'string' && parsed.effective_date) {
        const d = new Date(parsed.effective_date);
        if (!isNaN(d.getTime())) aiEffectiveDate = d.toISOString();
      }
      analysisFi = analysisOrNull(parsed.analysis_fi);
      audience = audienceOrNull(parsed.audience);
      if (isCommunityMarker(parsed.kind)) community = true;
      rawTopic = parsed.topic;
      rawPostType = parsed.post_type;
      author = authorOrNull(parsed.author);
    } catch {
      // not JSON, keep raw text summary
    }

    const title = (raw.cr436_title as string) || (raw.cr436_name as string) || (raw.Title as string) || '';

    // SourceFeedText (text, v1.0.0.3+) is authoritative; fall back through legacy Choice labels.
    const feed =
      (raw.cr436_sourcefeedtext as string) ||
      (raw.SourceFeedText as string) ||
      (raw['cr436_sourcefeed@OData.Community.Display.V1.FormattedValue'] as string) ||
      (raw.cr436_sourcefeed as string) ||
      (raw.SourceFeed as string) ||
      '';

    // --- Category: three-tier algorithm ---
    let category: Category = 'azure-ai';

    // 1. Absolute title rules override everything.
    if (/\bcowork\b/i.test(title)) {
      category = 'cowork';
    } else if (/copilot/i.test(title)) {
      category = 'copilot';
    } else if (/M365 Copilot|Copilot Studio|Microsoft Copilot Blog/i.test(feed)) {
      // 2. Feed-based: Copilot feed but non-copilot title -> reclassify.
      if (/\bpurview\b|\bdlp\b|data loss prevention|compliance|insider risk/i.test(title)) {
        category = 'security';
      } else if (
        /\bteams[:\s]|\boutlook[:\s]|\bviva[\s:]|\bonedrive[:\s]|\bsharepoint[:\s]|\bplanner\b/i.test(
          title,
        )
      ) {
        category = 'modernwork';
      } else if (/\bfoundry\b/i.test(title)) {
        category = 'foundry';
      } else if (/\bai model|\bazure ai\b|openai|gpt-[0-9]|\bmcp\b/i.test(title)) {
        category = 'ai';
      } else {
        category = 'modernwork';
      }
    } else if (/Partner Blog|Partner Network/i.test(feed)) {
      category = 'partner';
    } else if (/Foundry Blog/i.test(feed)) {
      category = 'foundry';
    } else if (/Microsoft AI Blog|AI Platform/i.test(feed)) {
      category = 'ai';
    } else if (/Security Blog|MSRC|Entra ID/i.test(feed)) {
      category = 'security';
    }

    // 3. Title keyword detection for generic feeds (Azure Updates, Azure Blog, M365 Roadmap).
    if (category === 'azure-ai') {
      if (/\bfoundry\b|foundry local|foundry agent|foundry sdk|foundry api|foundry lab/i.test(title)) {
        category = 'foundry';
      } else if (
        /\bai model|\bazure ai\b|\bai builder\b|cognitive service|openai\b|gpt-[0-9]|\bclaude\b|\bgemini\b|\bllama\b|phi-[0-9]|\bmistral\b|\bgrok\b|\bnemotron\b|model catalog|machine learning|\bazure ml\b|deep learning|neural network|\bmcp\b|model context protocol|ai agent|prompt flow|responsible ai|content safety|ai search|semantic kernel|\blangchain\b|embeddings?|\brag\b|retrieval.augmented|vector search|ai service|document intelligence|speech service|\btranslator\b|bot service|language understanding|text analytics|databricks.*agent|databricks.*ai|supervisor agent|knowledge assistant|ai.powered|generative ai|large language model|\bllm\b|\bslm\b|\bsre agent\b/i.test(
          title,
        )
      ) {
        category = 'ai';
      } else if (
        /\bteams[:\s]|\bteams\b.*meeting|\boutlook[:\s]|\boutlook\b.*mail|\bsharepoint[:\s]|\bviva[\s:]|\bonedrive[:\s]|\bloop[\s:]|\bwhiteboard\b|\bplanner\b|\byammer\b|microsoft 365 app|office app|\bbookings\b|\bclipchamp\b|\bstream\b.*video|\bsway\b/i.test(
          title,
        )
      ) {
        category = 'modernwork';
      } else if (
        /\bwaf\b|azure firewall|azure bastion|network security perimeter|threat intelligence|\bsentinel\b|defender for|encryption.in.transit|\bentra\b|conditional access|\bmfa\b|zero trust|web application firewall|key vault|\bpurview\b|\bdlp\b|data loss prevention|information protection|insider risk|compliance|security posture/i.test(
          title,
        )
      ) {
        category = 'security';
      } else if (/partner program|partner center|partner network|\bmpn\b|\bcsp\b.*resell|\bisv\b/i.test(title)) {
        category = 'partner';
      } else if (/databricks/i.test(title) && /\bagent\b|\bai\b|model|lakebase|supervisor|knowledge/i.test(title)) {
        category = 'ai';
      }
    }

    // --- Type + impact auto-detection from title ---
    let retireDate: Date | null = null;
    if (/retirement|retire|deprecat|end of support|will be retired/i.test(title)) {
      updateType = 'deprecation';
      retireDate = parseRetireDate(title);
      if (retireDate) {
        const now = new Date();
        const monthsUntil = (retireDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30);
        if (monthsUntil <= 6) {
          impact = 'critical';
          actionNeeded = true;
        } else if (monthsUntil <= 12) {
          impact = 'high';
          actionNeeded = true;
        } else {
          impact = 'medium';
          actionNeeded = false;
        }
      } else {
        impact = 'medium';
        actionNeeded = false;
      }
    } else if (/generally available|launched|\[launched\]/i.test(title)) {
      updateType = 'ga';
      if (/foundry|copilot|agent service|mcp/i.test(title)) impact = 'high';
    } else if (/rolling out|rollout|roll out/i.test(title)) {
      updateType = 'rollout';
    } else if (/in development|\[in development\]|private preview/i.test(title)) {
      updateType = 'development';
    } else if (/preview|in preview|\[in preview\]|public preview/i.test(title)) {
      updateType = 'preview';
    }

    // AI Builder sometimes classifies an item as type:"deprecation" even when
    // the title uses softer language ("will stop running X") that the regex
    // above missed. Retry the date parse so those items still land on the
    // timeline instead of falling into the null bucket.
    if (updateType === 'deprecation' && !retireDate) {
      retireDate = parseRetireDate(title);
    }

    // Security items from MSRC get elevated impact.
    if (/MSRC/i.test(feed) || /vulnerability|cve-|security advisory|patch/i.test(title)) {
      if (impact === 'medium') impact = 'high';
      actionNeeded = true;
    }

    // --- Subcategory + Microsoft update tag ---
    let subcategory: string | null = null;
    const subs = SUBCATEGORIES[category];
    if (subs) {
      for (const sub of subs) {
        if (sub.pattern.test(title)) {
          subcategory = sub.value;
          break;
        }
      }
    }

    let msTag: string | null = null;
    for (const tag of MS_UPDATE_TYPES) {
      if (tag.pattern.test(title)) {
        msTag = tag.label;
        break;
      }
    }

    // Stable id resolution. Every source below survives flow re-runs, so
    // bookmarks keyed on the chosen id stay valid across reloads.
    const id =
      (raw.cr436_table1id as string) ||
      (raw.cr436_ms_ai_updatesid as string) ||
      (raw.ms_ai_updatesid as string) ||
      (raw.cr436_sourceurl as string) ||
      (raw.SourceURL as string) ||
      null;

    if (!id) {
      // No stable id source — skip the row rather than invent a random id
      // that would fail to round-trip bookmarks. Surface via non-fatal
      // telemetry so the condition is debuggable if it ever fires in prod.
      reportNonFatal(
        new Error('parseDataverseItem: no stable id available'),
        'categorize.missing-id',
        { titleSnippet: title.slice(0, 80), feed },
      );
      return null;
    }

    const publishedDate = (raw.cr436_publisheddate as string) || (raw.PublishedDate as string) || '';
    // Lifecycle columns; missing or unparseable values become null so legacy
    // rows and a partial column rollout parse exactly as before.
    const lastChanged = dateStringOrNull(raw.clarito_lastchanged);

    return {
      id,
      title,
      summaryFI,
      publishedDate,
      sourceURL: (raw.cr436_sourceurl as string) || (raw.SourceURL as string) || '#',
      sourceFeed: feed || 'Azure Updates',
      category,
      subcategory,
      msTag,
      impact,
      updateType,
      actionNeeded,
      // Timeline position. Resolution order:
      //   1. AI-extracted effective_date (structured field from AI Builder,
      //      most reliable — the LLM sees the full body and can pick up
      //      dates that live in prose like "Rollout start: June CY2026").
      //   2. Deprecation: retirement date parsed from title.
      //   3. Anything else: bracketed rollout/launch window from the title
      //      ("[Rolling out: May 2026]") or Finnish/English month mention
      //      in the summary.
      //   4. Fallback: publishedDate.
      effectiveDate:
        aiEffectiveDate ??
        (updateType === 'deprecation'
          ? retireDate
            ? retireDate.toISOString()
            : null
          : parseRolloutDate(
              title,
              summaryFI,
              (raw.cr436_publisheddate as string) || (raw.PublishedDate as string),
            )?.toISOString() ??
            ((raw.cr436_publisheddate as string) || (raw.PublishedDate as string) || null)),
      analysisFi,
      audience,
      firstSeen: dateStringOrNull(raw.createdon),
      feedUpdated: dateStringOrNull(raw.clarito_feedupdated),
      lastChanged,
      activityDate: activityDateOf({ publishedDate, lastChanged }),
      // Official rows carry none of these, so their shape is unchanged.
      ...(community
        ? {
            kind: 'community' as const,
            topic: topicOrOther(rawTopic),
            postType: postTypeOrNews(rawPostType),
            author: author ?? authorOrNull(feed),
          }
        : {}),
    };
  } catch {
    return null;
  }
}
